import React from 'react';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { NodeViewProps } from '@tiptap/core';
import { TaskItemOptions } from '@tiptap/extension-task-item/src/task-item';

import {
    canLiftTaskItem,
    createNewEmptyTask,
    createNewEmptyTaskAbove,
    handleArrowDownInLastTask,
    handleArrowRightAtEndOfTask,
    handleShiftArrowDownInLastTask,
    handleShiftArrowRightAtEndOfTask,
    handleEmptyFirstTaskDeletion,
    handleEmptyFirstTaskToTitle,
    handleEmptyPreviousTaskDeletion,
    handleEmptyTaskForwardDeletion,
    handleEnterAcrossTextblocks,
    handleFirstSubTaskBackspace,
    handleFirstTitledTaskBackspace,
    handleModifiedDeleteShortcut,
    handleTaskItemAssignmentShortcut,
    handleTaskItemDueDateShortcut,
    handleTaskMergeBackward,
    handleTaskMergeForward,
    liftTaskItem,
    TaskItemAssignmentShortcutContext,
    TaskItemDueDateShortcutContext,
} from './taskListItemKeyboardHandlers';
import { createTaskToTitleInputRule } from './taskListTitleInputRules';
import TaskListItemDeleteButton from './TaskListItemDeleteButton';
import TaskListItemDragHandle, { TaskItemDragHandleComponentProps } from './TaskListItemDragHandle';
import TaskListItemActivityIndicator, {
    TaskItemActivityIndicatorComponentProps,
} from './TaskListItemActivityIndicator';
import { createTaskItemDragSourcePlugin } from './taskListItemDragSourcePlugin';
import { createTaskListTextSelectionPlugin } from './taskListTextSelectionPlugin';
import { createTaskListItemCopyExpansionPlugin } from './taskListItemCopyExpansionPlugin';
import { createTaskListItemCutChipRemovalPlugin } from './taskListItemCutChipRemovalPlugin';
import { createTaskListItemPastedLinesPlugin } from './taskListItemPastedLinesPlugin';
import { deleteTaskItem } from './taskListItemDeletionUtils';
import { setTaskItemCheckedWithSubTasks } from './taskListItemAttrUtils';
import { TaskListItemSchema } from './TaskListItemSchema';
import { Checkbox } from '@milanote/roma';
import type { TaskItemChipAttrs } from './taskListItemChipTypes';

declare module '@tiptap/core' {
    // oxlint-disable-next-line typescript/consistent-type-definitions -- augmenting an interface needs declaration merging
    interface Commands<ReturnType> {
        taskListItem: {
            /**
             * Lifts the selected tasks out of their list, as Shift-Tab does.
             */
            liftTaskItem: () => ReturnType;
        };
    }
}

/**
 * Props for the injected chips renderer. Typed only against `src/common` attr
 * types so the concrete `src/client` chips component can be injected via options
 * without `src/common` importing `src/client` (mirrors the checkbox/delete pattern).
 */
export type TaskItemChipsComponentProps = {
    attrs: TaskItemChipAttrs & { checked?: boolean };
    updateAttributes: (attrs: Partial<TaskItemChipAttrs>) => void;
    /** Whether the chips can be modified (from the owning board's write permission). */
    canModify?: boolean;
    /** The owning canvas element whose single-selection gates chip activation. */
    elementId: string;
};

type TaskListItemOptions = TaskItemOptions & {
    DeleteIconComponent: React.ComponentType;
    DragHandleComponent: React.ComponentType<TaskItemDragHandleComponentProps>;
    ChipsComponent: React.ComponentType<TaskItemChipsComponentProps>;
    /**
     * Renders the task's in-board activity badge (unseen assignment/reaction/label activity).
     * Optional so configurations that don't need it (e.g. Storybook) can omit it.
     */
    ActivityIndicatorComponent?: React.ComponentType<TaskItemActivityIndicatorComponentProps>;
    // Whether the task items can be modified (chips edited, items dragged out),
    // derived from the owning board's write permission where the editor's extensions
    // are configured. Unlike the editor's `isEditable` (which flips as the user
    // enters/leaves editing), this is stable for the editor's lifetime, so it can
    // be a static extension option.
    canModifyContent?: boolean;
    elementId: string;
    /** Fired after the user deletes a task via its trash button */
    onDeleteTask?: () => void;
    /**
     * Removes the whole task list element, which is what deleting the last task of an untitled
     * list amounts to (supplied by the client, which owns element trashing).
     */
    onDeleteTaskList?: () => void;
    /**
     * Opens the assignee picker for the task the assignment shortcut fired on (supplied
     * by the client, which owns the chip selection state). The shortcut is inactive
     * when omitted.
     */
    onAssignmentShortcut?: (context: TaskItemAssignmentShortcutContext) => void;
    /**
     * Opens the due-date picker for the task the due-date shortcut fired on (supplied
     * by the client, which owns the chip selection state). The shortcut is inactive
     * when omitted.
     */
    onDueDateShortcut?: (context: TaskItemDueDateShortcutContext) => void;
    /** Fired after the user toggles a task's checkbox (used by the client for UX feedback such as sounds). */
    onTaskCheckedChange?: (context: { checked: boolean }) => void;
    /** Fired after the user creates a new task item via the keyboard (Enter). */
    onTaskCreated?: () => void;
};

/**
 * Renders a task list item: the Roma checkbox plus the injected (client-supplied)
 * delete icon, drag handle, and chips components.
 */
const TaskListItemDisplay: React.FC<NodeViewProps> = (props) => {
    const { node, editor, getPos, updateAttributes, extension } = props;
    const {
        DeleteIconComponent,
        DragHandleComponent,
        ChipsComponent,
        ActivityIndicatorComponent,
        canModifyContent,
        elementId,
        onDeleteTask,
        onDeleteTaskList,
        onTaskCheckedChange,
    } = extension.options;

    // Shift-click makes the clicked task the master: its new checked state is applied
    // to all of its nested sub-tasks as well.
    const onClick = (event: React.MouseEvent) => {
        // On a read-only board the checkbox is display-only, so the click is left to
        // bubble to the board (where it selects the element), as the legacy task does.
        if (!canModifyContent) return;

        event.stopPropagation();

        const checked = !node.attrs.checked;

        if (event.shiftKey) {
            setTaskItemCheckedWithSubTasks({ editor, position: getPos(), checked });
        } else {
            updateAttributes({ checked });
        }

        onTaskCheckedChange?.({ checked });
    };

    /**
     * Updates task-item chip data and persists the containing task list immediately.
     */
    const updateChipAttributes = (attrs: Partial<TaskItemChipAttrs>) => {
        updateAttributes(attrs);
        editor.commands.flushPendingUpdate({});
    };

    const onDelete = () => deleteTaskItem({ editor, position: getPos(), onDeleteTask, onDeleteTaskList });

    return (
        <NodeViewWrapper className="task-item-row" data-task-id={node.attrs.taskId}>
            {/*
                The Roma checkbox is presentational, so the click-to-toggle lives on this
                non-editable wrapper (ProseMirror treats it as node chrome). `display: contents`
                keeps the checkbox's own `.RomaCheckboxBase` element as the row's flex child.
            */}
            <div contentEditable={false} onClick={onClick} style={{ display: 'contents' }}>
                <Checkbox.Base
                    className="hit-slop-t-md hit-slop-r-sm hit-slop-b-md hit-slop-l-xl"
                    isSelected={node.attrs.checked}
                    variant="tertiary"
                />
            </div>
            {/*
                Display-only badge, rendered as node chrome (see `TaskListItemActivityIndicator`)
                so it can never steal focus, clicks, or the caret from the editor.
            */}
            {ActivityIndicatorComponent && (
                <TaskListItemActivityIndicator
                    ActivityIndicatorComponent={ActivityIndicatorComponent}
                    taskId={node.attrs.taskId}
                    elementId={elementId}
                />
            )}
            {/*
                Rendered before `.task-item-main` (which holds nested sub-task rows and their own end-actions)
                so that, with equal z-index, this row's actions paint *below* any nested row's actions.
                That lets the deepest hovered row's gutter win the hover hit-test (see TaskListEditor.scss).
                Non-editable so ProseMirror treats it as node chrome, not content.
            */}
            <div className="task-item-end-actions" contentEditable={false}>
                <TaskListItemDeleteButton
                    show={node.attrs.checked && !!canModifyContent}
                    DeleteIcon={DeleteIconComponent}
                    onDelete={onDelete}
                />
                <TaskListItemDragHandle
                    DragHandleComponent={DragHandleComponent}
                    editor={editor}
                    elementId={elementId}
                    node={node}
                    getPos={getPos}
                    canDrag={!!canModifyContent}
                />
            </div>
            <div className="task-item-main">
                <NodeViewContent className="task-item-content" />
                <div className="task-item-chips" contentEditable={false}>
                    <ChipsComponent
                        attrs={node.attrs as TaskItemChipsComponentProps['attrs']}
                        updateAttributes={updateChipAttributes}
                        canModify={canModifyContent}
                        elementId={elementId}
                    />
                </div>
            </div>
        </NodeViewWrapper>
    );
};

/**
 * Decides whether ProseMirror should ignore an event that occurred inside a task item
 * node view (`true` = the node view owns the event, ProseMirror ignores it).
 *
 * Replaces the default `NodeView.stopEvent` for one reason: drags that start from the
 * item's drag handle belong to react-dnd, but the default preventDefaults every drag
 * event on a non-draggable node, which cancels the native HTML5 drag before react-dnd
 * can begin it. Everything else replicates the default's behaviour for this node
 * (non-draggable, selectable), using the node view's class names in place of its
 * dom/contentDOM references (which a custom stopEvent doesn't receive).
 */
export const stopTaskItemNodeViewEvent = ({ event }: { event: Event }): boolean => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest) return false;

    const isDragEvent = event.type.startsWith('drag');

    // Drags from the drag handle are react-dnd's: claim the event without
    // preventDefault-ing it so the native drag can begin.
    if (isDragEvent && target.closest('.task-list-item-drag-handle-container')) return true;

    // The nearest of the content hole / chrome regions tells us where the event
    // happened. Anything in the editable content hole is ProseMirror's (the default's
    // `isInElement` check); anything else in the row is node view chrome.
    const nearestRegion = target.closest('.task-item-content, .task-item-end-actions, .task-item-chips');
    if (nearestRegion?.classList.contains('task-item-content')) return false;

    const isDropEvent = event.type === 'drop';
    const isInput = ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable;
    if (isInput && !isDropEvent && !isDragEvent) return true;

    // Stop ProseMirror from dragging the (selectable, non-draggable) node itself,
    // e.g. via a text selection drag that starts on chrome.
    if (isDragEvent) {
        event.preventDefault();
        return true;
    }

    // These events are handled by ProseMirror.
    const isHandledByProsemirror =
        isDropEvent ||
        event.type === 'copy' ||
        event.type === 'paste' ||
        event.type === 'cut' ||
        event.type === 'mousedown';

    return !isHandledByProsemirror;
};

/**
 * A task item as the editor uses it: its schema, plus everything about editing one.
 *
 * The schema lives apart in `TaskListItemSchema`, so the node's shape can be composed without the
 * node view below - which pulls in Roma and SCSS the server cannot require.
 */
export const TaskListItem = TaskListItemSchema.extend<TaskListItemOptions>({
    addInputRules() {
        // Convert an empty first task into a title when "# " is typed (preserving any base rules).
        return [...(this.parent?.() ?? []), createTaskToTitleInputRule()];
    },

    addNodeView() {
        return ReactNodeViewRenderer(TaskListItemDisplay, {
            as: 'li',
            attrs: ({ node }) => ({
                'data-checked': String(node.attrs.checked),
            }),
            stopEvent: stopTaskItemNodeViewEvent,
        });
    },

    addProseMirrorPlugins() {
        return [
            // Preserve any base plugins.
            ...(this.parent?.() ?? []),
            // Fades the source item while it's being dragged out of the list
            createTaskItemDragSourcePlugin(),
            // Keeps the rows' hover actions out of the way of text selections
            createTaskListTextSelectionPlugin(),
            // Copies a whole task item when all of its text is selected
            createTaskListItemCopyExpansionPlugin(),
            // Clears a task's chips when a cut takes all of its text
            createTaskListItemCutChipRemovalPlugin(),
            // Pastes lines and bullets as tasks, keeping the nesting of the bullets
            createTaskListItemPastedLinesPlugin(),
        ];
    },

    addCommands() {
        return {
            liftTaskItem:
                () =>
                ({ editor, tr, dispatch }) => {
                    if (!dispatch) return canLiftTaskItem(editor, this.name);

                    // The lift dispatches its own transactions
                    tr.setMeta('preventDispatch', true);

                    return liftTaskItem(editor, this.name);
                },
        };
    },

    addKeyboardShortcuts() {
        const handleBackspace = () =>
            this.editor.commands.first((props) => [
                // Backspace straight after a markdown shortcut (e.g. "# ") puts the typed text back
                () => props.commands.undoInputRule(),
                () => handleEmptyFirstTaskToTitle(props, this.name),
                () => handleFirstTitledTaskBackspace(props, this.name),
                () => handleEmptyFirstTaskDeletion(props, this.name),
                () => handleEmptyPreviousTaskDeletion(props, this.name),
                () => handleTaskMergeBackward(props, this.name),
            ]) || handleFirstSubTaskBackspace(this.editor, this.name);
        const handleDelete = () =>
            this.editor.commands.first((props) => [
                () => handleEmptyTaskForwardDeletion(props, this.name),
                () => handleTaskMergeForward(props, this.name),
            ]);
        // A modified delete collapses an empty task. At a task boundary it otherwise acts as the plain key.
        const handleModifiedBackspace = () => handleModifiedDeleteShortcut(this.editor, this.name) || handleBackspace();
        const handleModifiedDelete = () => handleModifiedDeleteShortcut(this.editor, this.name) || handleDelete();

        return {
            Backspace: handleBackspace,
            Delete: handleDelete,
            Enter: () => {
                const splitTask = () =>
                    this.editor.commands.first((props) => [
                        () => createNewEmptyTaskAbove(props, this.name),
                        () => props.commands.splitListItem(this.name),
                        () => createNewEmptyTask(props, this.name),
                    ]);
                const handled = handleEnterAcrossTextblocks(this.editor, splitTask) || splitTask();
                if (handled) this.options.onTaskCreated?.();
                return handled;
            },
            'Mod-Delete': handleModifiedDelete,
            'Alt-Delete': handleModifiedDelete,
            'Mod-Backspace': handleModifiedBackspace,
            'Alt-Backspace': handleModifiedBackspace,
            // The TaskList extension's toggle would lift the tasks out of the document's only list
            'Mod-Shift-9': () => true,
            'Mod-Shift-2': () =>
                this.editor.commands.first((props) => [
                    () => handleTaskItemAssignmentShortcut(props, this.name, this.options.onAssignmentShortcut),
                ]),
            'Mod-Shift-1': () =>
                this.editor.commands.first((props) => [
                    () => handleTaskItemDueDateShortcut(props, this.name, this.options.onDueDateShortcut),
                ]),
            'Shift-Tab': () => {
                liftTaskItem(this.editor, this.name);
                // Intentionally always return true to prevent focus from leaving the editor
                return true;
            },
            Tab: () => {
                this.editor.commands.sinkListItem(this.name);
                // Intentionally always return true to prevent focus from leaving the editor
                return true;
            },
            // Fixes a bugs when moving the cursor - due to browser handling of NodeViewWrapper components
            ArrowDown: () => this.editor.commands.first((props) => [() => handleArrowDownInLastTask(props, this.name)]),
            'Shift-ArrowDown': () =>
                this.editor.commands.first((props) => [() => handleShiftArrowDownInLastTask(props, this.name)]),
            ArrowRight: () =>
                this.editor.commands.first((props) => [() => handleArrowRightAtEndOfTask(props, this.name)]),
            'Shift-ArrowRight': () =>
                this.editor.commands.first((props) => [() => handleShiftArrowRightAtEndOfTask(props, this.name)]),
        };
    },
});
